import { useRouter } from 'expo-router';
import {
  Boxes,
  Construction,
  Droplet,
  Droplets,
  Fence,
  Leaf,
  Mountain,
  Route,
  Shovel,
  Sparkles,
  Sprout,
  TreePine,
  Trees,
  Waves,
  Wrench,
} from 'lucide-react-native';
import { ComponentType, useEffect } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { PrimaryCTA, Tile } from '@/components/onboarding/Controls';
import {
  Eyebrow,
  GridHint,
  H1,
  Screen,
  SelCount,
} from '@/components/onboarding/Screen';
import { TileGrid } from '@/components/onboarding/TileGrid';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { LotBand } from '@/db/types';
import { qtyTotal, useOnboarding } from '@/store/onboarding';

const LOTS: { v: LotBand; title: string; Icon: ComponentType<any> }[] = [
  { v: 'q', title: 'Under ¼ acre', Icon: Leaf },
  { v: '1', title: '¼ – 1 acre', Icon: Leaf },
  { v: '5', title: '1 – 5 acres', Icon: TreePine },
  { v: '20', title: '5 – 20 acres', Icon: Trees },
  { v: '20+', title: '20+ acres', Icon: Trees },
];

const OUTSIDE_TILES: { k: string; label: string; Icon: ComponentType<any> }[] = [
  // Defaulted-on set first (varies by home_type — see store
  // OUTSIDE_DEFAULTS_BY_TYPE) so users see immediately that we've
  // pre-checked the typical exterior systems for their home shape.
  { k: 'lawn', label: 'Lawn / turf', Icon: Leaf },
  { k: 'woods', label: 'Trees & woods', Icon: TreePine },
  { k: 'gutters', label: 'Gutters', Icon: Wrench },
  { k: 'driveway', label: 'Driveway', Icon: Route },
  { k: 'siding', label: 'Siding / exterior', Icon: Construction },
  { k: 'outdoor_faucets', label: 'Outdoor faucets', Icon: Droplet },
  // Opt-in extras.
  { k: 'beds', label: 'Garden / beds', Icon: Sprout },
  { k: 'irrig', label: 'Irrigation', Icon: Droplets },
  { k: 'backflow', label: 'Backflow preventer', Icon: Droplet },
  { k: 'deck', label: 'Deck / patio', Icon: Boxes },
  { k: 'septic', label: 'Septic system', Icon: Shovel },
  { k: 'wellw', label: 'Well water', Icon: Droplets },
  { k: 'pool', label: 'Pool / spa', Icon: Waves },
  { k: 'outbld', label: 'Shed / pole barn', Icon: Sparkles },
  { k: 'fence', label: 'Fencing', Icon: Fence },
  { k: 'retain', label: 'Retaining walls', Icon: Mountain },
];

export default function PropertyScreen() {
  const router = useRouter();
  const { outside, toggleOutside, setOutsideCount, lotBand, setLotBand, mode } =
    useOnboarding();
  const applyOutsideDefaults = useOnboarding((s) => s.applyOutsideDefaults);

  // Pre-fill outside systems based on home_type the user picked
  // earlier. Store guards prevent overwriting a manual selection if
  // the user comes back via the back button.
  useEffect(() => {
    applyOutsideDefaults();
  }, [applyOutsideDefaults]);

  // Default lot size to under ¼ acre — the typical suburban / urban
  // lot. Pre-selecting the modal answer means the small-lot majority
  // keeps moving; users with bigger lots see all options.
  useEffect(() => {
    if (!lotBand) setLotBand('q');
  }, [lotBand, setLotBand]);

  const onContinue = () => {
    // Lifestyle step branches by mode itself (→ detail | generating).
    router.push('/(onboarding)/lifestyle' as never);
  };

  return (
    <Screen
      scroll
      footer={<PrimaryCTA label="Continue" disabled={!lotBand} onPress={onContinue} />}>
      <Eyebrow>7 · The property</Eyebrow>
      <H1>Now the grounds.</H1>
      <GridHint>How much land are you managing?</GridHint>

      {/* 5-column compact tile row instead of stacked OptionRows. The
          full-width rows ate the whole screen for a single answer
          when most users live on small lots and just tap the first
          tile — now the picker is a single line and the user can see
          all options at once. Each tile gets an icon stacked above
          its short label. */}
      <View style={lotStyles.row}>
        {LOTS.map(({ v, title, Icon }) => {
          const sel = lotBand === v;
          return (
            <Pressable
              key={v}
              onPress={() => setLotBand(v)}
              style={({ pressed }) => [
                lotStyles.tile,
                sel && lotStyles.tileSel,
                pressed && { opacity: 0.85 },
              ]}>
              <View style={[lotStyles.tileIcon, sel && lotStyles.tileIconSel]}>
                <Icon
                  size={18}
                  color={sel ? Palette.cream : Palette.forest}
                  strokeWidth={1.6}
                />
              </View>
              <Text
                style={[lotStyles.tileLabel, sel && lotStyles.tileLabelSel]}
                numberOfLines={2}>
                {title}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <GridHint>
        We&apos;ve pre-selected the typical exterior systems for your home
        type. Tap to de-select anything you don&apos;t have, or add others.
        Use +/- for multiple mowers, sheds, etc.
      </GridHint>

      <TileGrid>
        {OUTSIDE_TILES.map(({ k, label, Icon }) => {
          const count = outside[k] ?? 0;
          const selected = count > 0;
          return (
            <Tile
              key={k}
              label={label}
              selected={selected}
              count={count}
              icon={
                <Icon
                  size={18}
                  color={selected ? Palette.cream : Palette.forest}
                  strokeWidth={1.6}
                />
              }
              onPress={() => toggleOutside(k)}
              onIncrement={() => setOutsideCount(k, count + 1)}
              onDecrement={() => setOutsideCount(k, count - 1)}
            />
          );
        })}
      </TileGrid>

      <SelCount count={qtyTotal(outside)} />

    </Screen>
  );
}

const lotStyles = StyleSheet.create({
  row: {
    marginTop: Spacing.three,
    flexDirection: 'row',
    gap: 8,
  },
  tile: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingVertical: 12,
    paddingHorizontal: 6,
    gap: 8,
  },
  tileSel: {
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  tileIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileIconSel: {
    backgroundColor: Palette.forest,
  },
  tileLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 12,
    color: Palette.ink,
    textAlign: 'center',
    lineHeight: 14,
  },
  tileLabelSel: {
    color: Palette.forest,
    fontFamily: Fonts.sansBold,
  },
});
